import React, { useEffect, useState } from 'react';
import {
  Code2,
  Plus,
  Trash2,
  Pencil,
  Loader2,
  Power,
  PowerOff,
  ShieldAlert,
  Copy,
  Check,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import { adminApi } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

const PLACEMENT_LABEL = {
  head: '<head>',
  body_end: 'End of <body>',
};
const TARGET_LABEL = {
  all: 'All pages',
  public: 'Public pages only',
  app: 'App pages only',
};

function placementBadgeClass(p) {
  if (p === 'head') return 'bg-primary/10 text-primary border border-primary/30';
  return 'bg-secondary text-foreground border border-border';
}
function targetBadgeClass(t) {
  if (t === 'public') return 'bg-[hsl(var(--sky))]/30 text-foreground border border-border';
  if (t === 'app') return 'bg-[hsl(var(--mint))]/35 text-foreground border border-border';
  return 'bg-muted text-muted-foreground border border-border';
}

export function TrackingScriptsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await adminApi.listTrackingSnippets();
      setItems(r?.items || []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not load tracking snippets'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const onToggleActive = async (s) => {
    setBusyId(s.id);
    try {
      const updated = await adminApi.updateTrackingSnippet(s.id, { active: !s.active });
      setItems((prev) => prev.map((x) => (x.id === s.id ? updated : x)));
      toast.success(updated.active ? `Enabled "${updated.name}"` : `Disabled "${updated.name}"`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not update snippet'));
    } finally {
      setBusyId(null);
    }
  };

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await adminApi.deleteTrackingSnippet(deleteTarget.id);
      setItems((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      toast.success(`Deleted "${deleteTarget.name}"`);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not delete snippet'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="border-border" data-testid="tracking-scripts-panel">
      <CardContent className="p-0">
        <div className="p-4 border-b border-border flex items-center gap-3 flex-wrap">
          <Code2 size={18} className="text-primary" />
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold">Tracking & Scripts</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Paste raw HTML / JavaScript snippets (GA4, Meta Pixel, LinkedIn Insight Tag, GTM,
              Hotjar, anything custom). Choose placement and target surface. Scripts only run
              after the user accepts the cookie consent banner.
            </p>
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            data-testid="tracking-add-btn"
            className="rounded-xl"
          >
            <Plus size={14} className="mr-2" /> Add snippet
          </Button>
        </div>

        {loading ? (
          <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center" data-testid="tracking-empty-state">
            <ShieldAlert size={24} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No tracking snippets configured yet. Click <span className="font-mono-edge">Add snippet</span> to inject your first one.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((s) => (
              <li
                key={s.id}
                className="p-4 flex flex-col md:flex-row md:items-center gap-3"
                data-testid={`tracking-row-${s.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold truncate" data-testid="tracking-name">
                      {s.name}
                    </span>
                    {s.active ? (
                      <Badge className="bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border border-[hsl(var(--success))]/30 font-mono-edge text-[10px]">
                        Active
                      </Badge>
                    ) : (
                      <Badge className="bg-muted text-muted-foreground border border-border font-mono-edge text-[10px]">
                        Inactive
                      </Badge>
                    )}
                    <Badge className={`font-mono-edge text-[10px] ${placementBadgeClass(s.placement)}`}>
                      {PLACEMENT_LABEL[s.placement] || s.placement}
                    </Badge>
                    <Badge className={`font-mono-edge text-[10px] ${targetBadgeClass(s.target)}`}>
                      {TARGET_LABEL[s.target] || s.target}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 truncate font-mono-edge">
                    {(s.code || '').slice(0, 120).replace(/\s+/g, ' ')}
                    {(s.code || '').length > 120 ? '…' : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onToggleActive(s)}
                    disabled={busyId === s.id}
                    data-testid={`tracking-toggle-${s.id}`}
                  >
                    {busyId === s.id ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : s.active ? (
                      <PowerOff size={12} className="mr-1.5" />
                    ) : (
                      <Power size={12} className="mr-1.5" />
                    )}
                    {s.active ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditTarget(s)}
                    data-testid={`tracking-edit-${s.id}`}
                  >
                    <Pencil size={12} className="mr-1.5" /> Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteTarget(s)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`tracking-delete-${s.id}`}
                  >
                    <Trash2 size={12} />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {(createOpen || editTarget) && (
        <SnippetDialog
          snippet={editTarget}
          onClose={() => {
            setCreateOpen(false);
            setEditTarget(null);
          }}
          onSaved={(saved, mode) => {
            if (mode === 'create') {
              setItems((prev) => [saved, ...prev]);
              toast.success(`Added "${saved.name}"`);
            } else {
              setItems((prev) => prev.map((x) => (x.id === saved.id ? saved : x)));
              toast.success(`Saved "${saved.name}"`);
            }
            setCreateOpen(false);
            setEditTarget(null);
          }}
        />
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="tracking-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this snippet?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <span className="font-semibold">{deleteTarget?.name}</span> from
              every page it&apos;s currently injected on. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirmDelete}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="tracking-delete-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function SnippetDialog({ snippet, onClose, onSaved }) {
  const isEdit = !!snippet;
  const [name, setName] = useState(snippet?.name || '');
  const [code, setCode] = useState(snippet?.code || '');
  const [placement, setPlacement] = useState(snippet?.placement || 'head');
  const [target, setTarget] = useState(snippet?.target || 'all');
  const [active, setActive] = useState(snippet ? !!snippet.active : true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) {
      /* ignore */
    }
  };

  const onSubmit = async () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!code.trim()) {
      toast.error('Code is required');
      return;
    }
    setSaving(true);
    try {
      let saved;
      const payload = { name: name.trim(), code, placement, target, active };
      if (isEdit) {
        saved = await adminApi.updateTrackingSnippet(snippet.id, payload);
        onSaved(saved, 'edit');
      } else {
        saved = await adminApi.createTrackingSnippet(payload);
        onSaved(saved, 'create');
      }
    } catch (err) {
      toast.error(getErrorMessage(err, isEdit ? 'Could not save' : 'Could not create'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="tracking-snippet-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit “${snippet.name}”` : 'Add tracking snippet'}</DialogTitle>
          <DialogDescription>
            Paste the full <span className="font-mono-edge">&lt;script&gt;</span> block (or any
            HTML) exactly as the provider gives you. The frontend injector will respect cookie
            consent and only run scripts after the user accepts.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="tracking-name" className="text-xs">Snippet name</Label>
            <Input
              id="tracking-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Google Analytics 4 (G-XXXXXX)"
              data-testid="tracking-name-input"
              maxLength={120}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Placement</Label>
              <Select value={placement} onValueChange={setPlacement}>
                <SelectTrigger data-testid="tracking-placement-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="head">&lt;head&gt; — runs earliest</SelectItem>
                  <SelectItem value="body_end">End of &lt;body&gt; — after page render</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Inject on</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="tracking-target-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All pages (public + app)</SelectItem>
                  <SelectItem value="public">Public pages only (landing, login, pricing)</SelectItem>
                  <SelectItem value="app">App pages only (logged-in users)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="tracking-code" className="text-xs">Snippet code (HTML / JS)</Label>
              {code && (
                <button
                  type="button"
                  onClick={copyCode}
                  className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                  data-testid="tracking-copy-btn"
                >
                  {copied ? <Check size={11} /> : <Copy size={11} />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              )}
            </div>
            <Textarea
              id="tracking-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={`<!-- Paste your provider snippet here -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXX"></script>\n<script>\n  window.dataLayer = window.dataLayer || [];\n  function gtag(){dataLayer.push(arguments);}\n  gtag('js', new Date());\n  gtag('config', 'G-XXXXXX');\n</script>`}
              rows={10}
              className="font-mono-edge text-xs"
              data-testid="tracking-code-input"
              maxLength={20000}
            />
            <p className="text-[10px] text-muted-foreground">
              {code.length.toLocaleString()} / 20,000 characters
            </p>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <Switch
              checked={active}
              onCheckedChange={setActive}
              id="tracking-active"
              data-testid="tracking-active-switch"
            />
            <Label htmlFor="tracking-active" className="text-sm cursor-pointer">
              {active ? 'Active — snippet will be injected' : 'Inactive — saved but not injected'}
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving}
            data-testid="tracking-save-btn"
            className="rounded-xl"
          >
            {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
            {isEdit ? 'Save changes' : 'Add snippet'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TrackingScriptsPanel;
